import { estimateTokens, keepNewestWithin, type TokenCounter } from "./tokens";

// Clients bundle this module; server-only session config lives in
// `@workspace/services` (`domain/live-session.ts`).

export const LIVE_DATA_CHANNEL = "oai-events";
export const MAGNUS_CHAT_FUNCTION = "magnus_chat";

export const LIVE_VOICE_FLAG = "chat.live-voice.enable" as const;

export const LIVE_CALL_MAX_MS = 30 * 60_000;
export const LIVE_CALL_IDLE_MS = 5 * 60_000;

export const LIVE_VOICES = [
  { id: "cedar", label: "Cedar" },
  { id: "marin", label: "Marin" },
  { id: "gleam", label: "Gleam" },
  { id: "meridian", label: "Meridian" },
  { id: "vesper", label: "Vesper (British)" },
  { id: "willow", label: "Willow (Irish)" },
  { id: "quartz", label: "Quartz (Australian)" },
] as const;
export type LiveVoice = (typeof LIVE_VOICES)[number]["id"];
export const DEFAULT_LIVE_VOICE: LiveVoice = "cedar";

export function isLiveVoice(value: unknown): value is LiveVoice {
  return LIVE_VOICES.some((v) => v.id === value);
}

/** The provider's caps (https://developers.openai.com/api/docs/guides/live-conversations):
 *  instructions 16,384 tokens, input 128 messages and 8,192 tokens, an
 *  append 500 tokens. The budgets below stay under them. */
export const LIVE_INPUT_MAX_MESSAGES = 128;
/** What appends are trimmed to: `estimateTokens` can run under the real
 *  count on unusual text, and the provider rejects an append over the max. */
export const LIVE_APPEND_BUDGET_TOKENS = 400;

export const LIVE_INSTRUCTIONS_BUDGET_TOKENS = 12_000;
export const LIVE_INPUT_BUDGET_TOKENS = 6000;

export const LIVE_SWAP_HISTORY_MAX_MESSAGES = 400;
export const LIVE_SWAP_HISTORY_MAX_TOKENS = 24_000;

export type LiveHistoryTurn = { role: "user" | "assistant"; text: string };

export type LiveHistoryBudget = {
  maxMessages?: number;
  maxTokens?: number;
  /** Tokens already spoken for, like the greeting or resume line. */
  reserveTokens?: number;
  count?: TokenCounter;
};

/** The newest turns that fit the budget, oldest first. */
export function trimLiveHistory(
  turns: readonly LiveHistoryTurn[],
  budget: LiveHistoryBudget = {}
): LiveHistoryTurn[] {
  const count = budget.count ?? estimateTokens;
  const maxTokens =
    (budget.maxTokens ?? LIVE_INPUT_BUDGET_TOKENS) -
    (budget.reserveTokens ?? 0);
  const clean = turns
    .map((turn) => ({ role: turn.role, text: turn.text.trim() }))
    .filter((turn) => turn.text);
  return keepNewestWithin(
    clean,
    maxTokens,
    (turn) => count(turn.text) + 4,
    budget.maxMessages ?? LIVE_INPUT_MAX_MESSAGES - 1
  );
}

export type LiveFunctionCall = {
  callId: string;
  name: string;
  arguments: string;
  /** The backend response the call belongs to, when the event names it. */
  responseId?: string;
};

export type LiveBackendEvent =
  | { type: "response.created"; responseId: string }
  | { type: "response.function_call"; call: LiveFunctionCall }
  | {
      type: "response.finished";
      responseId: string;
      status: "completed" | "incomplete" | "failed";
    };

export type LiveEvent =
  | { type: "session.started"; sessionId: string }
  | { type: "session.closed"; reason: string }
  | {
      type: "transcript.delta";
      speaker: "user" | "assistant";
      delta: string;
      startMs: number;
    }
  | { type: "delegation.created"; delegationId: string }
  | { type: "backend"; event: LiveBackendEvent }
  | { type: "error"; message: string; code: string | null };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function str(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function parseBackendEvent(raw: unknown): LiveBackendEvent | null {
  if (!isRecord(raw)) {
    return null;
  }
  const response = isRecord(raw.response) ? raw.response : null;
  switch (raw.type) {
    case "response.created":
      return response && str(response.id)
        ? { type: "response.created", responseId: str(response.id) }
        : null;
    case "response.output_item.done": {
      const item = isRecord(raw.item) ? raw.item : null;
      if (item?.type !== "function_call" || !str(item.call_id)) {
        return null;
      }
      const responseId = str(raw.response_id) || str(response?.id);
      return {
        type: "response.function_call",
        call: {
          callId: str(item.call_id),
          name: str(item.name),
          arguments: str(item.arguments),
          ...(responseId ? { responseId } : {}),
        },
      };
    }
    case "response.completed":
    case "response.incomplete":
    case "response.failed":
      return response && str(response.id)
        ? {
            type: "response.finished",
            responseId: str(response.id),
            status: raw.type.slice("response.".length) as
              | "completed"
              | "incomplete"
              | "failed",
          }
        : null;
    default:
      return null;
  }
}

export function parseLiveEvent(data: unknown): LiveEvent | null {
  let raw: unknown = data;
  if (typeof data === "string") {
    try {
      raw = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (!isRecord(raw)) {
    return null;
  }
  switch (raw.type) {
    case "session.started": {
      const session = isRecord(raw.session) ? raw.session : null;
      return { type: "session.started", sessionId: str(session?.id) };
    }
    case "session.closed":
      return { type: "session.closed", reason: str(raw.reason) };
    case "session.input_transcript.delta":
    case "session.output_transcript.delta":
      return {
        type: "transcript.delta",
        speaker:
          raw.type === "session.input_transcript.delta" ? "user" : "assistant",
        delta: str(raw.delta),
        startMs: typeof raw.start_ms === "number" ? raw.start_ms : 0,
      };
    case "session.delegation.created": {
      const delegation = isRecord(raw.delegation) ? raw.delegation : null;
      return {
        type: "delegation.created",
        delegationId: str(delegation?.id),
      };
    }
    case "response.event": {
      const event = parseBackendEvent(raw.event);
      return event ? { type: "backend", event } : null;
    }
    case "error": {
      const error = isRecord(raw.error) ? raw.error : null;
      return {
        type: "error",
        message: str(error?.message) || "Voice session error",
        code: str(error?.code) || null,
      };
    }
    default:
      return null;
  }
}

let eventCounter = 0;
function eventId(prefix: string): string {
  eventCounter += 1;
  return `${prefix}_${Date.now().toString(36)}_${eventCounter}`;
}

export type LiveCommand = Record<string, unknown> & { type: string };

export function functionCallOutputCommand(
  callId: string,
  output: string
): LiveCommand {
  return {
    type: "response.item.create",
    event_id: eventId("fn_out"),
    item: { type: "function_call_output", call_id: callId, output },
  };
}

export function responseCreateCommand(): LiveCommand {
  return { type: "response.create", event_id: eventId("resume") };
}

/** Speak-now guidance for GPT-Live (≤500 tokens). */
export function commentaryCommand(content: string): LiveCommand {
  return {
    type: "session.commentary.append",
    event_id: eventId("commentary"),
    delegation_id: null,
    content,
  };
}

/** Facts for GPT-Live to use later without saying them now (≤500 tokens). */
export function thinkingCommand(content: string): LiveCommand {
  return {
    type: "session.thinking.append",
    event_id: eventId("thinking"),
    delegation_id: null,
    content,
  };
}

export function inputAudioMuteCommand(muted: boolean): LiveCommand {
  return {
    type: muted ? "session.input_audio.mute" : "session.input_audio.unmute",
    event_id: eventId("mute"),
  };
}

export function sessionCloseCommand(): LiveCommand {
  return { type: "session.close", event_id: eventId("close") };
}

/**
 * Responses delegation's tool protocol, over a `send`. Each function call
 * is started as soon as its item is done; its output goes back as a
 * `function_call_output`; once the backend response has finished *and*
 * every call in it has an output, exactly one `response.create` resumes
 * the backend. Every call gets an output — a pending one stalls the
 * backend, which refuses `response.create` until it is answered. A
 * repeated call event is ignored, and a call that names no response and
 * arrives outside one joins the next response to finish.
 */
export type LiveFunctionCallLoop = {
  handle: (event: LiveBackendEvent) => void;
  answer: (callId: string, output: string) => void;
  dispose: () => void;
};

const UNSCOPED = "unscoped";

type ResponseRecord = {
  calls: Map<string, boolean>;
  finished: boolean;
};

export function createLiveFunctionCallLoop(opts: {
  send: (command: LiveCommand) => void;
  onCall: (call: { callId: string; name: string; args: unknown }) => void;
  knownFunctions: readonly string[];
}): LiveFunctionCallLoop {
  const responses = new Map<string, ResponseRecord>();
  const responseOfCall = new Map<string, string>();
  const seenCalls = new Set<string>();
  let openResponseId: string | null = null;
  let disposed = false;

  const record = (responseId: string): ResponseRecord => {
    let entry = responses.get(responseId);
    if (!entry) {
      entry = { calls: new Map(), finished: false };
      responses.set(responseId, entry);
    }
    return entry;
  };

  const maybeResume = (responseId: string) => {
    const entry = responses.get(responseId);
    if (!entry?.finished) {
      return;
    }
    for (const answered of entry.calls.values()) {
      if (!answered) {
        return;
      }
    }
    responses.delete(responseId);
    if (entry.calls.size > 0) {
      opts.send(responseCreateCommand());
    }
  };

  const adoptUnscoped = (responseId: string, entry: ResponseRecord) => {
    const unscoped = responses.get(UNSCOPED);
    if (!unscoped || responseId === UNSCOPED) {
      return;
    }
    for (const [callId, answered] of unscoped.calls) {
      entry.calls.set(callId, answered);
      if (!answered) {
        responseOfCall.set(callId, responseId);
      }
    }
    responses.delete(UNSCOPED);
  };

  const answer = (callId: string, output: string) => {
    const responseId = responseOfCall.get(callId);
    const entry = responseId ? responses.get(responseId) : undefined;
    if (disposed || !(responseId && entry) || entry.calls.get(callId)) {
      return;
    }
    entry.calls.set(callId, true);
    responseOfCall.delete(callId);
    opts.send(functionCallOutputCommand(callId, output));
    maybeResume(responseId);
  };

  const run = (call: LiveFunctionCall) => {
    if (seenCalls.has(call.callId)) {
      return;
    }
    seenCalls.add(call.callId);
    const responseId = call.responseId || openResponseId || UNSCOPED;
    record(responseId).calls.set(call.callId, false);
    responseOfCall.set(call.callId, responseId);
    if (!opts.knownFunctions.includes(call.name)) {
      answer(
        call.callId,
        JSON.stringify({ error: `Unknown function "${call.name}".` })
      );
      return;
    }
    let args: unknown;
    try {
      args = call.arguments.trim() ? JSON.parse(call.arguments) : {};
    } catch {
      answer(
        call.callId,
        JSON.stringify({ error: "Arguments were not valid JSON." })
      );
      return;
    }
    opts.onCall({ callId: call.callId, name: call.name, args });
  };

  return {
    handle: (event) => {
      if (disposed) {
        return;
      }
      switch (event.type) {
        case "response.created":
          openResponseId = event.responseId;
          record(event.responseId);
          return;
        case "response.function_call":
          run(event.call);
          return;
        case "response.finished": {
          if (openResponseId === event.responseId) {
            openResponseId = null;
          }
          if (event.status === "failed") {
            const entry = responses.get(event.responseId);
            for (const callId of entry?.calls.keys() ?? []) {
              responseOfCall.delete(callId);
            }
            responses.delete(event.responseId);
            return;
          }
          const entry = record(event.responseId);
          entry.finished = true;
          adoptUnscoped(event.responseId, entry);
          maybeResume(event.responseId);
          return;
        }
        default:
          return;
      }
    },
    answer,
    dispose: () => {
      disposed = true;
      responses.clear();
      responseOfCall.clear();
      seenCalls.clear();
    },
  };
}

export type LiveTranscriptTurn = {
  role: "user" | "assistant";
  text: string;
  startMs: number;
};

/**
 * Groups transcript deltas into turns. Deltas carry no turn ids, so a turn
 * ends when the other speaker's text arrives. Fragments are appended as
 * received (they carry their own spacing).
 */
export function createLiveTranscript() {
  const turns: LiveTranscriptTurn[] = [];
  let open = false;
  return {
    push(speaker: "user" | "assistant", delta: string, startMs: number) {
      if (!delta) {
        return;
      }
      const last = turns.at(-1);
      if (open && last?.role === speaker) {
        last.text += delta;
      } else {
        turns.push({ role: speaker, text: delta, startMs });
        open = true;
      }
    },
    break() {
      open = false;
    },
    current(speaker: "user" | "assistant"): string {
      const last = turns.at(-1);
      return open && last?.role === speaker ? last.text.trim() : "";
    },
    turns(): LiveTranscriptTurn[] {
      return turns
        .map((t) => ({ ...t, text: t.text.trim() }))
        .filter((t) => t.text);
    },
  };
}

export const CANCEL_INTENT =
  /\b(never ?mind|forget (it|that|about it)|cancel (that|it)|don'?t bother|stop (that|it|looking|searching))\b/i;
